import { useQuery } from "@tanstack/solid-query";
import { type Component, createMemo, For, Index, Show } from "solid-js";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { getActiveCostByInterval } from "~/solidJs/entities/PremiumPopUp/helpers/IProducts";
import {
	TariffBtnModeEnum,
	tariffLokUp
} from "~/solidJs/entities/PremiumPopUp/helpers/tariffLookUpModes";
import { ProductCardV3, WorkWithBlockV3 } from "~/solidJs/entities/PremiumPopUp/ui/ProductCard";
import { TeamMethods } from "~/solidJs/entities/Teams";
import { useCustomContext } from "~/solidJs/shared";
import { ProductsApi } from "~/solidJs/shared/api/ProductsApi";
import { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types";
import { PricingPageContext } from "../methods";
import type { ProductsSectionPropsV3 } from "../model";
import { EnterpriseSection } from "./EnterpriseSection";

export const ProductsSectionV3: Component<ProductsSectionPropsV3> = (props) => {
	const store = useCustomContext(PricingPageContext);
	const productData = useQuery(() => ProductsApi.getNewProductListOptions());

	// Use teamInfo from context to avoid duplicate query
	const teamInfo = store.teamInfo;

	// Мемоизируем user один раз для всех карточек
	const user = createMemo(() => getUserStore());

	// Мемоизируем личный план один раз
	const personalPlan = createMemo(() => {
		return user()?.group || UserSubscriptionPlanEnum.Legacy_Freemium;
	});

	// Мемоизируем командный план один раз
	const teamPlan = createMemo(() => {
		const currentUser = user();
		const team = teamInfo.data;
		if (!currentUser || !team) return null;

		const isInTeam =
			TeamMethods.isUserTeamOwner(team) ||
			team.members?.some((m) => m.user_id === currentUser.id);
		const isTeamActive =
			team.team_subscription_status === "active" ||
			team.team_subscription_status === "cancelled" ||
			team.team_subscription_status === "due";

		if (isInTeam && isTeamActive && team.company_status === "active") {
			return team.tariff_name?.toLowerCase() || null;
		}
		return null;
	});

	return (
		<Show when={!store.isEnterprise()} fallback={<EnterpriseSection />}>
			<div class="flex w-full flex-col items-center gap-14">
				<div class="grid w-72 grid-cols-1 items-start justify-center gap-5 lg:gap-4 xl:w-full xl:grid-cols-4">
					<Index each={productData.data?.plans}>
						{(item) => {
							const activeCost = createMemo(() =>
								getActiveCostByInterval(item().costs, store.getSubscriptionLength())
							);

							// Проверяем, является ли текущая карточка личным планом
							const isPersonalPlan = createMemo(() => {
								return item().name.toLowerCase() === personalPlan().toLowerCase();
							});

							// Проверяем, является ли текущая карточка командным планом
							const isTeamPlanCard = createMemo(() => {
								const team = teamPlan();
								return team && item().name.toLowerCase() === team;
							});

							// Определяем btnMode
							const btnMode = createMemo(() => {
								// Если это личный или командный план - показываем Current
								if (isPersonalPlan() || isTeamPlanCard()) {
									return TariffBtnModeEnum.Current;
								}

								// Иначе используем стандартную логику через tariffLokUp
								return tariffLokUp[personalPlan() as keyof typeof tariffLokUp]?.[
									item().name
								];
							});

							return (
								<ProductCardV3
									selectedPlan={
										// Не передаем selectedPlan для командного плана,
										// чтобы кнопка оставалась серой как у личного плана
										isTeamPlanCard() ? undefined : props.forceSelectedPlan
									}
									isTrial={props.trialSelectedPlan === item().name}
									sourceGlobalId={props.sourceGlobalId}
									btnMode={btnMode()}
									nameId={item().name as UserSubscriptionPlanEnum}
									featuresV2={item().features}
									iconsFeatures={item().iconsFeatures}
									front_price={activeCost()?.price.toString() || "0"}
									front_price_ru={activeCost()?.price_ru.toString() || "0"}
									front_price_base={activeCost()?.price_base.toString() || "0"}
									front_price_base_ru={
										activeCost()?.price_base_ru.toString() || "0"
									}
									isFree={item().is_free}
									baseCostGeneration={productData.data?.baseCostGeneration}
									{...item()}
								/>
							);
						}}
					</Index>
				</div>
				<div class="custom-tw-hidden w-full grid-cols-4 items-start justify-center gap-4 xl:grid">
					<For each={productData.data?.plans}>
						{(item) => (
							<WorkWithBlockV3
								isMonthly={!item.is_free}
								nameId={item.name}
								isSelected={item.name === props.forceSelectedPlan}
								countCredits={item.count_credits}
								baseCostGeneration={productData.data?.baseCostGeneration}
							/>
						)}
					</For>
				</div>
			</div>
		</Show>
	);
};
